import styled from "@emotion/styled";
import { SparklesIcon } from "lucide-react";
import React from "react";
import { useDrawerState } from "@/store/drawer-state";
import { CHAT_MODE, useChatState } from "@/store/chat-state";
import { BODY_FONT_STACK, borders, breakpoints, colors, gradients, radius, shadows, spacing } from "../design-tokens";
import { Sentry } from "@/instrument";
import { WIDGET_ORIGIN } from "@/constants/widget-origin";
import { type BootstrapHandle, bootstrapWidgetWebComponent } from "@/utils/bootstrap-web-component";

type ContextTriggerProps = {
  buttonText?: string;
  summaryTitle?: string;
};

const EmotionButton = styled.button`
  margin-block: ${spacing["3xl"]}px;
  min-width: 230px;
  border-radius: ${radius.lg}px;
  ${borders.button}
  ${gradients.buttonBackground}
  ${shadows.button}
  backdrop-filter: blur(6px);
  color: ${colors.text};
  font-size: 16px;
  font-weight: 500;
  font-family: var(--pollen-font-family, ${BODY_FONT_STACK});
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md}px;
  padding: ${spacing.lg}px;
  cursor: pointer;
  outline: none;
  transition: all 200ms ease-in-out;

  &:hover {
    background:
      linear-gradient(
        0deg,
        color-mix(in srgb, var(--pollen-accent-color, #00abb8) 8%, transparent) 0%,
        color-mix(in srgb, var(--pollen-accent-color, #00abb8) 8%, transparent) 100%
      ),
      linear-gradient(
        0deg,
        color-mix(
            in srgb,
            var(
                --pollen-input-background-color,
                color-mix(in srgb, var(--pollen-accent-color, #00abb8) 8%, var(--pollen-card-background-color, #ffffff))
              )
              90%,
            white
          )
          0%,
        color-mix(
            in srgb,
            var(
                --pollen-input-background-color,
                color-mix(in srgb, var(--pollen-accent-color, #00abb8) 8%, var(--pollen-card-background-color, #ffffff))
              )
              90%,
            white
          )
          100%
      ),
      color-mix(in srgb, var(--pollen-accent-color, #00abb8) 12%, white);
    box-shadow:
      0px 0px 16px 0px color-mix(in srgb, var(--pollen-accent-color, #00abb8) 40%, transparent),
      0px 6px 24px 0px rgba(17, 17, 17, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  @media (max-width: ${breakpoints.md - 1}px) {
    width: 100%;
    justify-content: center;
  }
`;

const EmotionIcon = styled(SparklesIcon)`
  width: ${spacing["3xl"]}px;
  height: ${spacing["3xl"]}px;
  color: ${colors.accentSoft};
  fill: ${colors.accentSoft};
  stroke-width: 1;
  flex-shrink: 0;
`;

const ContextTriggerComponent: React.FC<ContextTriggerProps> = ({
  buttonText = "Summarise this page",
  summaryTitle = "Page summary",
}) => {
  const { openDrawer } = useDrawerState();
  const { setContextUrl, setUserQuery, setWidgetOrigin, setMode } = useChatState();

  const handleClick = () => {
    const currentUrl = window.location.href;
    if (currentUrl) {
      setContextUrl(currentUrl);
      setUserQuery(summaryTitle);
      setWidgetOrigin(WIDGET_ORIGIN.CONTEXT_TRIGGER);
      setMode(CHAT_MODE.INPUT);
      openDrawer();
    }
    else {
      Sentry.captureException(new Error("No current URL found in context trigger"));
      console.error("No current URL found");
    }
  };

  return (
    <div style={{ display: "inline-block", width: "100%" }}>
      <EmotionButton onClick={handleClick}>
        <EmotionIcon />
        {buttonText}
      </EmotionButton>
    </div>
  );
};

class ContextTriggerWebComponent extends HTMLElement {
  private handle: BootstrapHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    const buttonText = this.getAttribute("button-text") ?? undefined;
    const summaryTitle = this.getAttribute("summary-title") ?? undefined;
    this.handle = bootstrapWidgetWebComponent(this, {
      componentName: "context-trigger",
      render: () => <ContextTriggerComponent buttonText={buttonText} summaryTitle={summaryTitle} />,
    });
  }

  disconnectedCallback() {
    this.handle?.unmount();
    this.handle = null;
  }
}

export default ContextTriggerWebComponent;
